import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Combobox } from 'medere-ui-framework';
import { PatientSearchResult, ComboOption } from '../types/agenda.types';
import { searchPatients, getPatientById, getHealthEntitiesCombo, getHealthEntityPlansCombo } from '../services/AgendaService';
import { PatientRecordDTO } from '../../../interface/interface';
import { savePatient } from '../../../servicesHooks/DigitalRP/useDigitalRPService';
import PatientForm from '../../../components/PatientForm/PatientForm';
import './PatientSelector.scss';

function calculateAge(birthDateStr: string | null): number | null {
  if (!birthDateStr) return null;
  const birth = new Date(birthDateStr);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

export type PatientMode = 'identify' | 'unidentified';

export interface IdentifiedPatientData {
  patientId: number;
  patient: PatientSearchResult;
  phone: string;
  email: string;
}

export interface UnidentifiedPatientData {
  patientReference: string;
  patientPhone: string;
  patientDocument: string;
  healthEntityPlanId: number | null;
  benefitNumber: string;
}

export type PatientSelectorValue =
  | { mode: 'identify'; data: IdentifiedPatientData | null }
  | { mode: 'unidentified'; data: UnidentifiedPatientData };

interface PatientSelectorProps {
  /** Initial patient ID to preload (optional). */
  initialPatientId?: number | null;
  /** Whether the component is visible (controls effects). */
  visible: boolean;
  /** Show phone/email fields for identified patients (agenda uses them, spontaneous may not). */
  showContactFields?: boolean;
  /** When true, replaces "Sin registrar" with "Crear paciente" (for spontaneous flow). */
  allowCreate?: boolean;
  /** When false, hides the "Registrado" / "Sin registrar" tabs (flows that only accept registered patients). */
  showModeTabs?: boolean;
  /** Called whenever the selection state changes. */
  onChange: (value: PatientSelectorValue) => void;
  /** Request a reset — increment this number to trigger a full reset. */
  resetKey?: number;
}

export default function PatientSelector({
  initialPatientId,
  visible,
  showContactFields = true,
  allowCreate = false,
  showModeTabs = true,
  onChange,
  resetKey = 0,
}: PatientSelectorProps) {
  const [patientMode, setPatientMode] = useState<PatientMode>('identify');

  // Identified patient state
  const [patientQuery, setPatientQuery] = useState('');
  const [patientResults, setPatientResults] = useState<PatientSearchResult[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<PatientSearchResult | null>(null);
  const [searchingPatient, setSearchingPatient] = useState(false);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');

  // Unidentified patient state
  const [unidentifiedName, setUnidentifiedName] = useState('');
  const [unidentifiedPhone, setUnidentifiedPhone] = useState('');
  const [unidentifiedDocument, setUnidentifiedDocument] = useState('');
  const [selectedHealthEntity, setSelectedHealthEntity] = useState<ComboOption | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<ComboOption | null>(null);
  const [unidentifiedBenefitNumber, setUnidentifiedBenefitNumber] = useState('');

  // Create patient state
  const createPatientRef = useRef<PatientRecordDTO | null>(null);
  const [createFormValid, setCreateFormValid] = useState(false);
  const [savingPatient, setSavingPatient] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const healthEntityPlansParams = useMemo(
    () => ({ healthEntityId: selectedHealthEntity?.id ?? 0 }),
    [selectedHealthEntity],
  );

  // ─── Reset ──────────────────────────────────────
  useEffect(() => {
    setPatientMode('identify');
    setPatientQuery('');
    setPatientResults([]);
    setSelectedPatient(null);
    setSearchingPatient(false);
    setPhone('');
    setEmail('');
    setUnidentifiedName('');
    setUnidentifiedPhone('');
    setUnidentifiedDocument('');
    setSelectedHealthEntity(null);
    setSelectedPlan(null);
    setUnidentifiedBenefitNumber('');
    createPatientRef.current = null;
    setCreateFormValid(false);
    setSavingPatient(false);
    setCreateError(null);
    setShowCreateModal(false);
  }, [resetKey]);

  // ─── Load initial patient ──────────────────────
  useEffect(() => {
    if (!visible || !initialPatientId) return;
    getPatientById(initialPatientId).then((patient) => {
      setSelectedPatient(patient);
      setPhone(patient.mobilePhone || patient.phoneNumber || '');
      setEmail(patient.email || '');
    }).catch(console.error);
  }, [visible, initialPatientId]);

  // ─── Patient search ─────────────────────────────
  useEffect(() => {
    if (patientQuery.trim().length < 2) { setPatientResults([]); return; }
    const timeout = setTimeout(() => {
      setSearchingPatient(true);
      searchPatients(patientQuery.trim())
        .then(setPatientResults)
        .catch(console.error)
        .finally(() => setSearchingPatient(false));
    }, 400);
    return () => clearTimeout(timeout);
  }, [patientQuery]);

  // Reset plan when health entity changes
  useEffect(() => { setSelectedPlan(null); }, [selectedHealthEntity]);

  // ─── Notify parent on every change ──────────────
  useEffect(() => {
    if (patientMode === 'identify') {
      onChange({
        mode: 'identify',
        data: selectedPatient ? {
          patientId: selectedPatient.id,
          patient: selectedPatient,
          phone,
          email,
        } : null,
      });
    } else {
      onChange({
        mode: 'unidentified',
        data: {
          patientReference: unidentifiedName,
          patientPhone: unidentifiedPhone,
          patientDocument: unidentifiedDocument,
          healthEntityPlanId: selectedPlan?.id ?? null,
          benefitNumber: unidentifiedBenefitNumber,
        },
      });
    }
  }, [patientMode, selectedPatient, phone, email, unidentifiedName, unidentifiedPhone, unidentifiedDocument, selectedPlan, unidentifiedBenefitNumber]);

  const handleSelectPatient = useCallback((patient: PatientSearchResult) => {
    setSelectedPatient(patient);
    setPhone(patient.mobilePhone || patient.phoneNumber || '');
    setEmail(patient.email || '');
    setPatientQuery('');
    setPatientResults([]);
  }, []);

  const handleSaveNewPatient = useCallback(async () => {
    const patient = createPatientRef.current;
    if (!patient) return;
    setSavingPatient(true);
    setCreateError(null);
    try {
      const saved = await savePatient(patient, 1, 0);
      const savedId = saved?.patient ?? saved?.medereentity;
      if (!savedId) throw new Error('No se pudo obtener el ID del paciente creado');
      const fullPatient = await getPatientById(savedId);
      setShowCreateModal(false);
      setPatientMode('identify');
      handleSelectPatient(fullPatient);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error al crear el paciente';
      setCreateError(message);
    } finally {
      setSavingPatient(false);
    }
  }, [handleSelectPatient]);

  return (
    <>
      {showModeTabs && (
        <div className="seg-tabs" style={{ marginBottom: '0.75rem' }}>
          <button type="button" className={patientMode === 'identify' ? 'on' : ''} onClick={() => setPatientMode('identify')}>
            Registrado
          </button>
          {!allowCreate && (
            <button type="button" className={patientMode === 'unidentified' ? 'on' : ''} onClick={() => setPatientMode('unidentified')}>
              Sin registrar
            </button>
          )}
        </div>
      )}

      {patientMode === 'identify' ? (
        !selectedPatient ? (
          <>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'stretch' }}>
              <span className="modal-search-wrapper" style={{ flex: 1 }}>
                <InputText
                  value={patientQuery}
                  onChange={(e) => setPatientQuery(e.target.value)}
                  placeholder="Nombre del paciente registrado..."
                  className="modal-search-input"
                />
              </span>
              <Button
                icon="pi pi-plus"
                className="p-button-outlined p-button-sm"
                tooltip="Crear nuevo paciente"
                tooltipOptions={{ position: 'top' }}
                style={{ borderColor: '#3CB8BA', color: '#3CB8BA' }}
                onClick={() => {
                  createPatientRef.current = null;
                  setCreateFormValid(false);
                  setCreateError(null);
                  setShowCreateModal(true);
                }}
              />
            </div>
            {searchingPatient && <div className="modal-search-loading">Buscando...</div>}
            {patientResults.length > 0 && (
              <div className="patient-results">
                {patientResults.map((p) => (
                  <div key={p.id} className="patient-result-item" onClick={() => handleSelectPatient(p)}>
                    <div className="patient-result-item__info">
                      <span className="patient-result-item__name">{p.fullName}</span>
                      <span className="patient-result-item__detail">
                        {p.documentType} {p.document}
                        {p.coverageName && ` · ${p.coverageName}`}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <div className="selected-patient-card">
              <div className="selected-patient-card__info">
                <div className="selected-patient-card__name-row">
                  <span className="selected-patient-card__name">{selectedPatient.fullName}</span>
                </div>
                <span className="selected-patient-card__detail">
                  {selectedPatient.documentType} {selectedPatient.document}
                  {calculateAge(selectedPatient.birthDate) !== null && ` · ${calculateAge(selectedPatient.birthDate)} años`}
                  {selectedPatient.coverageName && ` · ${selectedPatient.coverageName}`}
                  {selectedPatient.planName && ` ${selectedPatient.planName}`}
                </span>
              </div>
              <Button label="Quitar" className="p-button-text selected-patient-card__change" onClick={() => setSelectedPatient(null)} />
            </div>
            {showContactFields && (
              <div className="patient-contact-row">
                <div className="patient-contact-field">
                  <label>Teléfono</label>
                  <InputText value={phone} onChange={(e) => setPhone(e.target.value)} />
                </div>
                <div className="patient-contact-field">
                  <label>Email</label>
                  <InputText value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
              </div>
            )}
          </>
        )
      ) : (
        <>
          <div className="unidentified-field">
            <label>Nombre de referencia <span className="required">*</span></label>
            <InputText value={unidentifiedName} onChange={(e) => setUnidentifiedName(e.target.value)} className="unidentified-input" />
          </div>
          <div className="patient-contact-row">
            <div className="patient-contact-field">
              <label>Teléfono <span className="required">*</span></label>
              <InputText value={unidentifiedPhone} onChange={(e) => setUnidentifiedPhone(e.target.value)} />
            </div>
            <div className="patient-contact-field">
              <label>Documento (si lo tiene)</label>
              <InputText value={unidentifiedDocument} onChange={(e) => setUnidentifiedDocument(e.target.value)} placeholder="Opcional" />
            </div>
          </div>
          <h4 className="modal-section__title" style={{ marginTop: '0.75rem' }}>COBERTURA <span style={{ fontWeight: 400 }}>(opcional)</span></h4>
          <div className="patient-contact-row">
            <div className="patient-contact-field">
              <Combobox label="Obra social" getItems={getHealthEntitiesCombo} optionLabel="name" value={selectedHealthEntity} setValue={setSelectedHealthEntity} placeholder="Seleccionar" />
            </div>
            <div className="patient-contact-field">
              <Combobox label="Plan" getItems={getHealthEntityPlansCombo} params={healthEntityPlansParams} optionLabel="name" value={selectedPlan} setValue={setSelectedPlan} placeholder="Opcional" disable={!selectedHealthEntity} />
            </div>
          </div>
          <div className="unidentified-field" style={{ marginTop: '0.5rem' }}>
            <label>Nº afiliado</label>
            <InputText value={unidentifiedBenefitNumber} onChange={(e) => setUnidentifiedBenefitNumber(e.target.value)} placeholder="Opcional" className="unidentified-input" />
          </div>
        </>
      )}

      <Dialog
        visible={showCreateModal}
        onHide={() => { if (!savingPatient) setShowCreateModal(false); }}
        header="Crear paciente"
        modal
        style={{ width: '700px' }}
        closable={!savingPatient}
        draggable={false}
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              label="Cancelar"
              className="p-button-text"
              disabled={savingPatient}
              onClick={() => setShowCreateModal(false)}
            />
            <Button
              label="Guardar paciente"
              icon="pi pi-check"
              disabled={!createFormValid || savingPatient}
              loading={savingPatient}
              onClick={handleSaveNewPatient}
              style={{ backgroundColor: '#3CB8BA', borderColor: '#3CB8BA' }}
            />
          </div>
        }
      >
        <PatientForm
          initialPatient={null}
          onPatientChange={(p) => { createPatientRef.current = p; }}
          onValidityChange={setCreateFormValid}
          hideTitle
          hideObservations
        />
        {createError && (
          <div className="modal-error-banner" style={{ marginTop: '0.5rem' }}>
            <i className="pi pi-exclamation-triangle" />
            <span>{createError}</span>
          </div>
        )}
      </Dialog>
    </>
  );
}
